import { Logo } from './Layout';
import { cn } from './ui';

/**
 * The side of a school's sign-in page beside the form: its logo and name, its background image (or the school color),
 * and its welcome text. Settings shows the same panel as a preview.
 */
export function SignInWelcome({
  name,
  logoUrl,
  imageUrl,
  headline,
  message,
  className,
}: {
  name: string;
  logoUrl: string | null;
  imageUrl: string | null;
  /** Hidden when empty. */
  headline: string;
  /** Hidden when empty. */
  message: string;
  className?: string;
}) {
  return (
    <div className={cn('relative overflow-hidden bg-brand-900', className)}>
      {imageUrl ? (
        <>
          <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
          {/* Dark enough at the top, middle, and bottom for white text over any photo. */}
          <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/30 to-black/70" />
        </>
      ) : (
        <>
          <div className="absolute -top-40 -right-40 size-[32rem] rounded-full bg-brand-700/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-20 size-96 rounded-full bg-brand-500/20 blur-3xl" />
        </>
      )}
      <div className="relative flex h-full flex-col justify-between gap-8 p-12 text-white">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="size-14 rounded-xl bg-white object-contain p-1.5" />
          ) : (
            <div className="flex size-14 items-center justify-center rounded-xl bg-white/10 text-2xl font-semibold">{name.charAt(0)}</div>
          )}
          <p className="text-xl font-semibold">{name}</p>
        </div>
        {(headline || message) && (
          <div className="max-w-lg">
            {headline && <h2 className="text-4xl leading-tight font-semibold tracking-tight text-balance">{headline}</h2>}
            {message && <p className={cn('whitespace-pre-line', headline && 'mt-4', imageUrl ? 'text-white/90' : 'text-brand-100')}>{message}</p>}
          </div>
        )}
        <p className={cn('flex items-center gap-2 text-xs', imageUrl ? 'text-white/75' : 'text-brand-200/80')}>
          <Logo className="size-5" /> Edvora Campus Asset
        </p>
      </div>
    </div>
  );
}
